import { useEffect, useState } from "react"
import { Badge, Card, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import API from "../../common/API"

interface Vehiculo {
    idInterno: string
    vin: string
    estado: string
    estadoTransferencia: string
    tipoVehiculo: string
    marca: string
    modelo: string
    anio: number | null
    color: string
    matricula: string
    nombrePropietario: string
    puertoEntrada: string
    fechaRegistroPlaca: string
    cedulaPropietario: string | null
}

const RecordVehiculos = (props: { id?: string; name?: string; matricula?: string }) => {
    const { id, name, matricula } = props
    const { t } = useTranslation()
    const [records, setRecords] = useState<Vehiculo[]>([])

    const bindRecords = async () => {
        try {
            const map = new Map<string, Vehiculo>()

            // 1. If matricula/VIN is provided directly, fetch it first
            if (matricula && matricula.trim()) {
                const cleanMat = matricula.trim()
                const res = await API.postAction("Vehiculos/List", {
                    matricula: cleanMat,
                    page: 0,
                    quantity: 20,
                })
                if (res.data.status === "ok" && res.data.response?.length > 0) {
                    res.data.response.forEach((v: Vehiculo) => {
                        const key = v.vin || v.matricula || v.idInterno
                        if (key) map.set(key, v)
                    })
                }
            }

            // 2. Search by cedula (exact, uses index)
            if (id) {
                const res = await API.postAction("Vehiculos/List", {
                    cedula: id,
                    page: 0,
                    quantity: 20,
                })
                if (res.data.status === "ok" && res.data.response?.length > 0) {
                    res.data.response.forEach((v: Vehiculo) => {
                        const key = v.vin || v.matricula || v.idInterno
                        if (key) map.set(key, v)
                    })
                }
            }

            // 3. Fallback: search by name if map is empty
            if (map.size === 0 && name && name.trim()) {
                const res = await API.postAction("Vehiculos/List", {
                    search: name.trim(),
                    page: 0,
                    quantity: 20,
                })
                if (res.data.status === "ok" && res.data.response?.length > 0) {
                    res.data.response.forEach((v: Vehiculo) => {
                        const key = v.vin || v.matricula || v.idInterno
                        if (key) map.set(key, v)
                    })
                }
            }

            setRecords(Array.from(map.values()))
        } catch (error) {
            console.error("Error fetching vehicle records:", error)
        }
    }

    useEffect(() => {
        if (id || name || matricula) bindRecords()
    }, [id, name, matricula])

    if (records.length === 0) return null

    const isMatchQuery = (v: Vehiculo) => {
        if (!matricula) return false
        const clean = matricula.trim().toUpperCase()
        return (
            (v.matricula && v.matricula.toUpperCase() === clean) ||
            (v.vin && v.vin.toUpperCase() === clean) ||
            (v.idInterno && v.idInterno.toUpperCase() === clean)
        )
    }

    return (
        <Card className="my-4 shadow-sm border overflow-hidden" style={{ borderRadius: "0.75rem" }}>
            <Card.Header className="display-4 d-flex align-items-center justify-content-between">
                <div>
                    <i className="fas fa-car me-2 text-primary" />
                    {t("Vehículos")}
                </div>
                {matricula && (
                    <span className="badge bg-dark text-warning font-monospace px-2 py-1">
                        <i className="fa fa-search me-1"></i> {matricula}
                    </span>
                )}
            </Card.Header>
            {matricula && (
                <div className="bg-light border-bottom px-3 py-2 d-flex align-items-center justify-content-between text-dark small">
                    <div className="d-flex align-items-center gap-2">
                        <i className="fa fa-car text-primary"></i>
                        <span>{t("Consulta realizada con vehículo")}: <strong className="font-monospace text-dark">{matricula}</strong></span>
                    </div>
                    <Badge bg="primary" className="font-monospace">
                        {t("Vehículo Consultado")}
                    </Badge>
                </div>
            )}
            <Card.Body className="p-0">
                <div style={{ overflowX: "auto" }}>
                    <Table size="sm" hover className="align-middle mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>{t("Placa")}</th>
                                <th>{t("Matrícula")}</th>
                                <th>{t("Tipo")}</th>
                                <th>{t("Marca / Modelo")}</th>
                                <th>{t("Año")}</th>
                                <th>{t("Color")}</th>
                                <th>{t("VIN / Chasis")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {records.map((v, i) => {
                                const isQueried = isMatchQuery(v)
                                return (
                                    <tr key={i} className={isQueried ? "table-warning fw-semibold" : ""}>
                                        <td>
                                            <span className={`badge ${isQueried ? "bg-dark text-warning" : "bg-secondary text-white"} font-monospace fs-6 px-2 py-1`}>
                                                {v.idInterno || v.matricula || "—"}
                                            </span>
                                            {isQueried && (
                                                <Badge bg="warning" text="dark" className="ms-2">
                                                    Consultado
                                                </Badge>
                                            )}
                                        </td>
                                        <td className="font-monospace">{v.matricula || "—"}</td>
                                        <td>{v.tipoVehiculo || "—"}</td>
                                        <td className="fw-semibold">
                                            {v.marca} {v.modelo}
                                        </td>
                                        <td>{v.anio ?? "—"}</td>
                                        <td>{v.color || "—"}</td>
                                        <td><small className="text-muted font-monospace">{v.vin || "—"}</small></td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                </div>
            </Card.Body>
        </Card>
    )
}

export default RecordVehiculos
